// @ts-nocheck
// TODO: align with current types (Phase 4a) — bucket 2/3 + DS strictness regressions
/**
 * User Create Modal
 *
 * Modal dialog for creating new users.
 */

import React from 'react';
import { useCreateUser } from '../../hooks';
import { FormModal } from '../shared';
import { UserForm, type UserFormData } from './UserForm';
import { PasswordLinkResult, useLinkCloseGuard } from './PasswordLinkResult';
import { Button, useToast, ToastAction } from '@lognai/synapse-design-system-v2';
import { useNavigate } from 'react-router-dom';
import type { CreatedUser } from '../../api';

export interface UserCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * No password is typed here: admins never set one. Create answers with a
 * one-time set-password link, which this modal shows once — the dialog stays
 * open on it so the admin can copy it before it is gone.
 */
export function UserCreateModal({ open, onOpenChange, onSuccess }: UserCreateModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const createUser = useCreateUser();
  const [formRef, setFormRef] = React.useState<HTMLFormElement | null>(null);
  const [created, setCreated] = React.useState<CreatedUser | null>(null);

  const guard = useLinkCloseGuard(!!created?.passwordLink, onOpenChange);
  const { reset: resetCreateUser } = createUser;
  const { reset: resetGuard } = guard;

  // The link is shown once: never carry it over to the next opening, in this
  // component or in the mutation cache.
  React.useEffect(() => {
    if (!open) {
      setCreated(null);
      resetCreateUser();
      resetGuard();
    }
  }, [open, resetCreateUser, resetGuard]);

  const handleSubmit = async (data: UserFormData) => {
    try {
      const { roleId, ...rest } = data;
      const user = await createUser.mutateAsync(roleId ? { ...rest, roleId } : rest);
      setCreated(user ?? ({ email: data.email } as CreatedUser));
      onSuccess?.();

      if (roleId) return;

      /**
       * No role was chosen (or the company has none), so the account can sign
       * in and do nothing (QA ledger ADM-003). Say so and offer the one screen
       * that fixes it. (The PERM-007 token-size blocker that kept the role
       * picker out of this form went away when permissions left the JWT.)
       */
      const userId = user?.id;
      toast({
        title: 'User created — no role yet',
        description: `${data.email} can sign in but has no permissions until a role is assigned.`,
        ...(userId
          ? {
              action: (
                <ToastAction
                  altText="Assign a role"
                  onClick={() => navigate(`/admin/users/${userId}/roles`)}
                >
                  Assign role
                </ToastAction>
              ),
            }
          : {}),
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create user',
        variant: 'destructive',
      });
    }
  };

  if (created) {
    const userLabel = created.fullName || created.email;
    return (
      <FormModal
        open={open}
        onOpenChange={guard.requestOpenChange}
        title="Create New User"
        description={`${created.email} has been created.`}
        size="md"
        hideFooter
      >
        <div className="space-y-4">
          {created.passwordLink ? (
            <PasswordLinkResult
              link={created.passwordLink}
              userLabel={userLabel}
              onCopied={guard.markCopied}
            />
          ) : (
            <p role="status" className="text-sm">
              The user was created, but their set-password link could not be generated. Generate a
              password link from the user's menu and share it with them.
            </p>
          )}
          <div className="flex justify-end">
            <Button type="button" onClick={() => guard.requestOpenChange(false)}>
              Done
            </Button>
          </div>
        </div>
        {guard.confirmDialog}
      </FormModal>
    );
  }

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Create New User"
      description="Add a new user. You'll get a one-time link for them to set their password."
      size="md"
      submitLabel="Create User"
      isSubmitting={createUser.isPending}
      onSubmit={() => {
        // Trigger form submission from FormModal
        formRef?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }}
    >
      <div ref={(ref) => setFormRef(ref?.querySelector('form') || null)}>
        <UserForm onSubmit={handleSubmit} isSubmitting={createUser.isPending} />
      </div>
    </FormModal>
  );
}
